// The renewal card on the Orders section (KOVA-797) — a port of the mobile app's
// `features/orders/model/action-needed.ts`. Eligibility is the server's: the card shows only what the banner feed
// returns, never a client-side threshold or timer (spec §2.6 B1). Of the feed it reads the two renewal actions —
// payment first, then the repeat intake — and shows one at a time, at the escalation the server computed.
//
// Unlike the mobile model this one carries no `url`: the web does not open `Banner.url` (it is app-stamped), the
// payment CTA goes to the local `/payment/update` and the intake to its own seam (spec §2.6 B12, §5).
import type { BannerAction, BannerEscalation, OrdersBanner } from '@/api/medOrders'

/** The actions the renewal card draws. `health_check_expired` stays contract-only — no design draws it. */
export type RenewalAction = Extract<BannerAction, 'payment_needed' | 'health_check_due'>

export type RenewalActionItem = OrdersBanner & { action: RenewalAction }

/** Figma's AlertBanner tiers: neutral → Info, warning → Warning, critical → Error. */
export type RenewalTier = 'info' | 'warning' | 'error'

export interface RenewalBannerModel {
	id: string
	action: RenewalAction
	tier: RenewalTier
	title: string
	body: string
	buttonLabel: string
	/** Set on a health-check action (it binds to that in-progress order's badge); `null` on a payment. */
	orderId: string | null
}

// "Payment and intake actions should never be shown simultaneously": payment wins, whatever the escalations.
const ACTION_PRECEDENCE: Record<RenewalAction, number> = {
	payment_needed: 1,
	health_check_due: 2,
}

const ESCALATION_TIER: Record<BannerEscalation, RenewalTier> = {
	neutral: 'info',
	warning: 'warning',
	critical: 'error',
}

interface ActionCopy {
	title: string
	body: Record<BannerEscalation, string>
	buttonLabel: string
}

// Titles and bodies as in the mobile app and Figma (Figma's curly apostrophe); the buttons are Figma's — the
// mobile app says "Update" / "Complete" (docs/architecture/orders.md, Design deviations).
const ACTION_COPY: Record<RenewalAction, ActionCopy> = {
	payment_needed: {
		title: 'Update payment info',
		body: {
			neutral:
				'We couldn’t process your payment. Update it now to keep your next medication shipment on track and avoid delays.',
			warning:
				'Your payment still needs your attention. Update it now to avoid delaying your next medication shipment.',
			critical: 'Your next medication shipment may be delayed. Update your payment method now to keep things moving.',
		},
		buttonLabel: 'Update payment',
	},
	health_check_due: {
		title: 'Complete your intake form',
		body: {
			neutral:
				'Complete a quick 3-minute intake so your clinician can prescribe your next medication and help avoid delays with your next shipment.',
			warning:
				'Your intake is still needed before your clinician can prescribe your next medication. Complete it now to avoid delays with your next shipment.',
			critical:
				'Your next medication shipment is at risk of delay. Complete your intake now so your clinician can prescribe your next medication without further delay.',
		},
		buttonLabel: 'Complete now',
	},
}

function isRenewalAction(banner: OrdersBanner): banner is RenewalActionItem {
	return banner.type === 'action_needed' && (banner.action === 'payment_needed' || banner.action === 'health_check_due')
}

/** The feed's renewal actions, payment before intake; ties keep the feed's order (the sort is stable). */
export function renewalActionItems(banners: readonly OrdersBanner[]): RenewalActionItem[] {
	return banners.filter(isRenewalAction).sort((a, b) => ACTION_PRECEDENCE[a.action] - ACTION_PRECEDENCE[b.action])
}

export function selectRenewalAction(banners: readonly OrdersBanner[]): RenewalActionItem | null {
	return renewalActionItems(banners)[0] ?? null
}

/** Missing (or unknown — the mapper made it `null`) reads as neutral; the client never decays or restarts it. */
export function renewalEscalation(banner: Pick<OrdersBanner, 'escalation'>): BannerEscalation {
	return banner.escalation ?? 'neutral'
}

/** The one renewal card to draw, or `null` when the feed has no renewal action. */
export function renewalBannerModel(banners: readonly OrdersBanner[]): RenewalBannerModel | null {
	const item = selectRenewalAction(banners)
	if (item === null) return null
	const escalation = renewalEscalation(item)
	const copy = ACTION_COPY[item.action]
	return {
		id: item.id,
		action: item.action,
		tier: ESCALATION_TIER[escalation],
		title: copy.title,
		body: copy.body[escalation],
		buttonLabel: copy.buttonLabel,
		orderId: item.orderId,
	}
}
